import { CheckIcon, ClipboardIcon } from '@heroicons/react/outline'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Button } from './ui/button'
import { useUser } from '@clerk/clerk-react'
import { useRouter } from '@/hooks/useRouter'
import { useAxios } from '@/contexts/AxiosProvider'
import { useAxiosQuery } from '@/hooks/useAxiosQuery'

type MeetingDetailsScreenProps = {
  onClickJoin: (id: string) => void | Promise<void>
  _handleOnCreateMeeting: () => Promise<{ meetingId?: string; err?: string }>
  participantName: string
  setParticipantName: React.Dispatch<React.SetStateAction<string>>
  onClickStartMeeting: () => void
}

export function MeetingDetailsScreen({
  onClickJoin,
  _handleOnCreateMeeting,
  participantName,
  setParticipantName,
  onClickStartMeeting,
}: MeetingDetailsScreenProps) {
  const [meetingIdInput, setMeetingIdInput] = useState('') // was "inviteCode"
  const [inviteCodeError, setInviteCodeError] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [isCreateMeetingClicked, setIsCreateMeetingClicked] = useState(false)
  const [isJoinMeetingClicked, setIsJoinMeetingClicked] = useState(false)
  const [userCount, setUserCount] = useState(0)

  const { queryParams, query } = useRouter()
  const axios = useAxios()
  const { data } = useAxiosQuery<{
    isPublic?: boolean
    usersCount?: number
    accessLimit?: number
    hallId?: string
    ownerId?: string
  }>({
    url: `/api/hall/${query.id}`,
  })
  const { user } = useUser()
  const isOwner = useMemo(() => Boolean(user?.id && data?.ownerId && user.id === data.ownerId), [user?.id, data?.ownerId])

  useEffect(() => {
    if (data?.isPublic === false && typeof data.usersCount === 'number') {
      setUserCount(data.usersCount)
    }
    if (queryParams?.inviteCode || query?.id || data?.isPublic) {
      setIsJoinMeetingClicked(true)
      if (typeof queryParams?.inviteCode === 'string') {
        setMeetingIdInput(queryParams.inviteCode)
      }
    }
  }, [queryParams, query, data])

  const handleJoin = async () => {
    try {
      if (data?.isPublic || isOwner) {
        if (data?.hallId) onClickJoin(data.hallId)
      } else {
        const code = meetingIdInput.trim()
        if (!code) {
          setInviteCodeError(true)
          return
        }
        const res = await axios.get(`/api/join-hall/${encodeURIComponent(code)}`)
        if (res.status === 404) setInviteCodeError(true)
        if (res.data?.hallId) onClickJoin(res.data.hallId)
      }
    } catch {
      // no-op; you could toast an error if desired
    }
  }

  const shouldDisableSubmit = useMemo(() => {
    if (participantName.trim().length < 3) return true
    if (
      data?.isPublic === false &&
      typeof data.usersCount === 'number' &&
      typeof data.accessLimit === 'number'
    ) {
      return data.usersCount >= data.accessLimit
    }
    return false
  }, [participantName, data])

  return (
    <form
      className="flex flex-1 flex-col justify-center w-full md:p-[6px] sm:p-1 p-1.5"
      onSubmit={(e) => {
        e.preventDefault()
        if (isCreateMeetingClicked) onClickStartMeeting()
        else handleJoin()
      }}
    >
      {isCreateMeetingClicked ? (
        <div className="border border-solid border-gray-400 rounded-xl px-4 py-3 flex items-center justify-center">
          <p className="dark:text-white text-base">{`Meeting code : ${meetingIdInput}`}</p>
          <Button
            variant="outline"
            className="ml-2"
            onClick={() => {
              navigator.clipboard.writeText(meetingIdInput)
              setIsCopied(true)
              setTimeout(() => setIsCopied(false), 3000)
            }}
          >
            {isCopied ? (
              <CheckIcon className="h-5 w-5 text-green-400" />
            ) : (
              <ClipboardIcon className="h-5 w-5 dark:text-white" />
            )}
          </Button>
        </div>
      ) : isJoinMeetingClicked && !data?.isPublic ? (
        <>
          {data?.isPublic === false && typeof data.accessLimit === 'number' && (
            <h1 className="text-lg mb-4 font-bold text-center">
              This is a private room: there are {Math.max(data.accessLimit - userCount, 0)} seats
              left.
            </h1>
          )}

          {isOwner ? (
            <div className="flex w-full justify-center">
              <p className="dark:text-white text-sm text-center font-medium">
                You're the room owner. No invite code required.
              </p>
            </div>
          ) : typeof queryParams?.inviteCode === 'string' ? (
            <div className="flex w-full justify-center">
              <p className="dark:text-white text-lg text-center font-semibold max-w-[200px] truncate">
                {`Invite code: ${meetingIdInput}`}
              </p>
            </div>
          ) : (
            <input
              value={meetingIdInput}
              onChange={(e) => setMeetingIdInput(e.target.value)}
              placeholder="Enter Invite Code"
              className="px-4 py-3 rounded-xl dark:text-white w-full text-center bg-gray-600 dark:bg-background dark:border-white border-2 disabled:cursor-not-allowed disabled:opacity-20"
            />
          )}

          {inviteCodeError && (
            <p className="text-xs text-red-600 mt-1 text-center">
              Please enter a valid invite code
            </p>
          )}
        </>
      ) : null}

      {(isCreateMeetingClicked || isJoinMeetingClicked) && (
        <>
          <input
            value={participantName}
            onChange={(e) => setParticipantName(e.target.value)}
            placeholder="Enter your name"
            maxLength={30}
            className="px-4 py-3 mt-5 rounded-xl text-black dark:text-white w-full text-center dark:bg-background dark:border-white border-2"
          />

          <Button
            type="submit"
            variant="outline"
            disabled={shouldDisableSubmit}
            className="w-full text-white hover:text-white px-2 py-3 rounded-xl mt-5 border-2 border-white bg-indigo-700 hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-20"
          >
            {isCreateMeetingClicked ? 'Join meeting' : 'Join Hall'}
          </Button>
        </>
      )}

      {!isCreateMeetingClicked && !isJoinMeetingClicked && (
        <div className="w-full md:mt-0 mt-4 flex flex-col">
          <div className="flex items-center justify-center flex-col w-full">
            <Button
              variant="outline"
              className="w-full bg-purple-350 dark:text-white px-2 py-3 rounded-xl"
              onClick={async () => {
                const { meetingId, err } = await _handleOnCreateMeeting()
                if (meetingId) {
                  setMeetingIdInput(meetingId) // keep local display value
                  setIsCreateMeetingClicked(true)
                } else {
                  toast(`${err}`, {
                    position: 'bottom-left',
                    autoClose: 4000,
                    hideProgressBar: true,
                    closeButton: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: 'light',
                  })
                }
              }}
            >
              Create a meeting
            </Button>

            <Button
              disabled={shouldDisableSubmit}
              variant="outline"
              className="w-full dark:bg-gray-600 dark:text-white px-2 py-3 rounded-xl mt-5"
              onClick={() => setIsJoinMeetingClicked(true)}
            >
              Join the debate!
            </Button>
          </div>
        </div>
      )}
    </form>
  )
}
